import { ReportDataRecord } from 'src/types/admin/reportsConfig.types';

/**
 * Adds client-generated row IDs to an array of data records.
 *
 * This utility function transforms raw API/S3 data by adding unique
 * numeric identifiers to each row. The numeric index approach is chosen
 * for optimal AG-Grid performance with large datasets (100K+ records).
 *
 * ## Why This Exists
 *
 * Report data fetched from APIs or S3 may not have unique identifiers for each row.
 * AG-Grid requires unique row IDs for efficient row selection, virtual scrolling,
 * and stable row identification during sorting/filtering operations.
 *
 * See CLIENT_ROW_ID_FIELD in reportsConfig.types.ts for detailed documentation
 * on the design decision to use numeric indices.
 *
 * @param data - Raw data records without row IDs
 * @returns Data records with _row_id field added
 *
 * @example
 * const rawData = [{ name: 'A' }, { name: 'B' }];
 * const withIds = addClientRowIds(rawData);
 * // Result: [{ _row_id: 0, name: 'A' }, { _row_id: 1, name: 'B' }]
 */
export const addClientRowIds = (data: Record<string, unknown>[]): ReportDataRecord[] => {
  return data.map((record, index) => ({
    _row_id: index,
    ...record,
  }));
};
